import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ImageIcon,
  ImagePlus,
  X,
  Video,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "../../components/ui/checkbox";
import {
  donationSchemesAPI,
  donationTypeMasterAPI,
  donationMainCategoryMasterAPI,
  donationSubCategoryMasterAPI,
  projectsAPI,
  type DonationScheme,
  type DonationSchemeFormValues,
  type DonationSchemeAmountType,
  type DonationTypeMaster,
  type DonationMainCategoryMaster,
  type DonationSubCategoryMaster,
  type Project,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";
import DonationCampaignDetailsModal from "./DonationCampaignDetailsModal";

const MAX_CAMPAIGN_MEDIA = 10;
/** Maximum allowed amount: ₹1 Cr */
const MAX_AMOUNT_INR = 10_000_000;

function parseFormAmount(value: string): number {
  const n = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

function getAmountRangeError(value: string, label: string): string | null {
  if (!value.trim()) {
    return null;
  }
  const amount = parseFormAmount(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    return `${label} must be greater than 0`;
  }
  if (amount > MAX_AMOUNT_INR) {
    return `${label} cannot exceed ₹1 Cr (1,00,00,000)`;
  }
  return null;
}

function AmountFieldMessage({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  const error = getAmountRangeError(value, label);
  if (error) {
    return <p className="mt-1 text-xs text-destructive">{error}</p>;
  }
  return (
    <p className="mt-1 text-xs text-muted-foreground">
      Maximum ₹1 Cr (1,00,00,000)
    </p>
  );
}

type CampaignMediaPhase = "before" | "after";

type MediaPreviewItem = {
  /** Local UI key */
  id: string;
  /** API media row id when available */
  serverMediaId?: string | number;
  url: string;
  isVideo: boolean;
  isNew: boolean;
  file?: File;
};

function isValidServerMediaId(id: string | number | undefined): boolean {
  if (id === undefined || id === null || id === "") {
    return false;
  }
  const s = String(id);
  if (
    s.startsWith("new-") ||
    s.startsWith("existing-") ||
    s.startsWith("server-")
  ) {
    return false;
  }
  return /^\d+$/.test(s) || /^[0-9a-f-]{36}$/i.test(s);
}

function getSchemeMediaRaw(
  scheme: DonationScheme,
  phase: CampaignMediaPhase
): unknown {
  const record = scheme as unknown as Record<string, unknown>;
  if (phase === "before") {
    const beforeRaw =
      scheme.before_image ??
      scheme.before_media ??
      record.before_images ??
      record.before_medias;
    if (beforeRaw) {
      return beforeRaw;
    }
    return (
      scheme.medias ??
      scheme.media ??
      record.donation_scheme_media ??
      record.media_files ??
      record.scheme_media ??
      record.images
    );
  }
  return (
    scheme.after_image ??
    scheme.after_media ??
    record.after_images ??
    record.after_medias
  );
}

const initialFormState: DonationSchemeFormValues = {
  title: "",
  sub_title: "",
  type_of_donation: "",
  category: "",
  sub_category: "",
  description: "",
  amount_needed: "",
  is_active: true,
  start_date: "",
  end_date: "",
  before_details: "",
  after_details: "",
  allow_donation_after_target: false,
  is_cash_donation: true,
  amount_type: "fixed",
  fixed_amount: "",
  min_contribution: "",
  max_contribution: "",
  bannerFile: null,
  beforeMediaFiles: [],
  afterMediaFiles: [],
  project: "",
};

function isVideoFile(file: File | string): boolean {
  if (typeof file === "string") {
    return /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(file);
  }
  return file.type.startsWith("video/");
}

function normalizeAmountType(value?: string | null): DonationSchemeAmountType {
  if (value === "variable") {
    return "variable";
  }
  return "fixed";
}

function normalizeSchemeMediaRaw(raw: unknown): unknown[] {
  if (!raw) {
    return [];
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        /* single URL string */
      }
    }
    return [trimmed];
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  return [raw];
}

function parseSchemeMedia(
  scheme: DonationScheme,
  phase: CampaignMediaPhase
): Omit<MediaPreviewItem, "isNew" | "file">[] {
  const raw = getSchemeMediaRaw(scheme, phase);
  const items = normalizeSchemeMediaRaw(raw);
  if (items.length === 0) {
    return [];
  }
  return items
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `existing-${index}`,
          url: item,
          isVideo: isVideoFile(item),
        };
      }
      const record = item as Record<string, unknown>;
      const url = String(
        record.url ??
          record.file ??
          record.media ??
          record.image ??
          record.media_url ??
          record.file_url ??
          ""
      );
      const serverMediaId =
        record.id ??
        record.pk ??
        record.media_id ??
        record.donation_scheme_media_id;
      const validId = isValidServerMediaId(
        serverMediaId as string | number | undefined
      )
        ? (serverMediaId as string | number)
        : undefined;
      return url
        ? {
            id: validId ? `server-${validId}` : `existing-${index}`,
            serverMediaId: validId,
            url,
            isVideo: isVideoFile(url),
          }
        : null;
    })
    .filter((x): x is Omit<MediaPreviewItem, "isNew" | "file"> => x !== null);
}

function buildRetainedMediaPayload(items: MediaPreviewItem[]) {
  const existing = items.filter(item => !item.isNew);
  const retainedMediaIds = existing
    .map(item => item.serverMediaId)
    .filter((id): id is string | number => isValidServerMediaId(id));
  const retainedMediaUrls = [
    ...new Set(
      existing
        .map(item => item.url)
        .filter(url => Boolean(url) && !url.startsWith("blob:"))
    ),
  ];
  return { retainedMediaIds, retainedMediaUrls };
}

function revokeBlobUrls(items: MediaPreviewItem[]) {
  items.forEach(item => {
    if (item.isNew && item.url.startsWith("blob:")) {
      URL.revokeObjectURL(item.url);
    }
  });
}

type CampaignMediaUploadSectionProps = {
  phase: CampaignMediaPhase;
  items: MediaPreviewItem[];
  required: boolean;
  onFileChange: (
    phase: CampaignMediaPhase,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onRemove: (phase: CampaignMediaPhase, id: string) => void;
};

function CampaignMediaUploadSection({
  phase,
  items,
  required,
  onFileChange,
  onRemove,
}: CampaignMediaUploadSectionProps) {
  const label =
    phase === "before" ? "Before campaign media" : "After campaign media";
  const inputId = `campaign-${phase}-media`;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <Label htmlFor={inputId}>
          {label} (images/videos) {required && "*"}
        </Label>
        <span className="text-xs text-muted-foreground">
          {items.length}/{MAX_CAMPAIGN_MEDIA}
        </span>
      </div>
      <Input
        id={inputId}
        type="file"
        accept="image/*,video/*"
        multiple
        className="mt-1"
        disabled={items.length >= MAX_CAMPAIGN_MEDIA}
        onChange={e => onFileChange(phase, e)}
      />
      <p className="mt-1 text-xs text-muted-foreground">
        Upload up to {MAX_CAMPAIGN_MEDIA} images or videos for the{" "}
        {phase === "before" ? "before" : "after"} section. On edit, add new
        files to append; removing only affects unsaved new uploads until saved.
      </p>
      {items.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          {items.map(item => (
            <div
              key={item.id}
              className="group relative overflow-hidden rounded-lg border bg-muted/30"
            >
              {item.isVideo ? (
                <video
                  src={item.url}
                  className="h-28 w-full object-cover"
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={item.url}
                  alt={`${phase} campaign media`}
                  className="h-28 w-full object-cover"
                />
              )}
              <div className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                {item.isVideo ? (
                  <span className="inline-flex items-center gap-0.5">
                    <Video className="h-3 w-3" />
                    Video
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-0.5">
                    <ImageIcon className="h-3 w-3" />
                    Image
                  </span>
                )}
                {item.isNew && " · New"}
              </div>
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="absolute right-1 top-1 h-7 w-7 opacity-90"
                onClick={() => onRemove(phase, item.id)}
                aria-label="Remove media"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getErrorMessage(err: unknown, fallback: string) {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as {
        response?: {
          data?: { message?: string; detail?: string; error?: string };
        };
      }
    ).response;
    return (
      response?.data?.message ??
      response?.data?.detail ??
      response?.data?.error ??
      (err as unknown as Error)?.message ??
      fallback
    );
  }
  return (err as Error)?.message ?? fallback;
}

function parseAmount(value?: string | number | null): number {
  if (value === "" || value === null) {
    return 0;
  }
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Compact INR for cards: 1100 → ₹1K, 2500 → ₹2.5K, 150000 → ₹1.5L */
function formatInrCompact(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) {
    return "₹0";
  }
  if (amount === 0) {
    return "₹0";
  }

  if (amount >= 10_000_000) {
    const cr = amount / 10_000_000;
    const label = cr % 1 === 0 ? String(cr) : cr.toFixed(1).replace(/\.0$/, "");
    return `₹${label}Cr`;
  }
  if (amount >= 100_000) {
    const lakhs = amount / 100_000;
    const label =
      lakhs % 1 === 0 ? String(lakhs) : lakhs.toFixed(1).replace(/\.0$/, "");
    return `₹${label}L`;
  }
  if (amount >= 1000) {
    const k = amount / 1000;
    if (k >= 100) {
      return `₹${Math.round(k)}K`;
    }
    const wholeK = Math.round(k);
    if (Math.abs(k - wholeK) < 0.15) {
      return `₹${wholeK}K`;
    }
    const label = (Math.round(k * 10) / 10).toFixed(1).replace(/\.0$/, "");
    return `₹${label}K`;
  }

  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

function getSchemeFundingStats(scheme: DonationScheme) {
  const goal = parseAmount(scheme.amount_needed);
  const raised = parseAmount(scheme.amount_collected);
  const percent =
    goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  return { goal, raised, percent };
}

type DonationCampaignListCardProps = {
  scheme: DonationScheme;
  onView: (scheme: DonationScheme) => void;
  onEdit: (scheme: DonationScheme) => void;
  onDelete: (scheme: DonationScheme) => void;
};

function DonationCampaignListCard({
  scheme,
  onView,
  onEdit,
  onDelete,
}: DonationCampaignListCardProps) {
  const { goal, raised, percent } = getSchemeFundingStats(scheme);
  const isActive = scheme.is_active !== false;

  return (
    <article className="flex h-full flex-col rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-4 flex items-start justify-between gap-3">
        <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-foreground">
          {scheme.title}
        </h3>
        <span
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
            isActive
              ? "bg-primary/10 text-primary"
              : "bg-muted text-muted-foreground"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              isActive ? "bg-primary" : "bg-muted-foreground"
            }`}
          />
          {isActive ? "Active" : "Inactive"}
        </span>
      </div>

      {goal > 0 ? (
        <>
          <div className="mb-2 flex min-w-0 items-baseline gap-x-1.5 gap-y-0.5">
            <span className="shrink-0 text-2xl font-bold tabular-nums leading-none text-primary">
              {formatInrCompact(raised)}
            </span>
            <span className="min-w-0 truncate text-sm font-medium leading-snug text-muted-foreground">
              <span className="font-normal">of </span>
              {formatInrCompact(goal)}
            </span>
          </div>
          <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="mb-4 text-xs text-muted-foreground">
            {percent}% funded
          </p>
        </>
      ) : (
        <p className="mb-4 line-clamp-2 text-sm text-muted-foreground">
          {scheme.sub_title?.trim() ??
            scheme.description?.trim() ??
            "No funding goal set"}
        </p>
      )}

      <div className="mt-auto flex gap-2">
        <Button
          type="button"
          size="sm"
          className="h-9 min-w-0 flex-[8] gap-1 px-2"
          onClick={() => onView(scheme)}
        >
          <Eye className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate text-xs sm:text-sm">View</span>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-9 min-w-0 flex-[8] gap-1 px-2"
          onClick={() => void onEdit(scheme)}
        >
          <Edit className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate text-xs sm:text-sm">Edit</span>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="deleteIcon"
          className="h-9 min-w-0 flex-[2] px-0"
          onClick={() => onDelete(scheme)}
          aria-label="Delete campaign"
        >
          <Trash2 className="h-4 w-4 shrink-0 text-destructive hover:text-gray-500" />
        </Button>
      </div>
    </article>
  );
}

function getSubCategoryLabel(item: DonationSubCategoryMaster): string {
  return item.title?.trim() ?? item.name?.trim() ?? String(item.id);
}

function resolveSubCategoryFormValue(
  raw: string | number | null | undefined,
  subCategories: DonationSubCategoryMaster[]
): string {
  if (raw === null || raw === undefined) {
    return "";
  }
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return "";
  }
  const byId = subCategories.find(s => String(s.id) === trimmed);
  if (byId) {
    return String(byId.id);
  }
  const byTitle = subCategories.find(
    s => getSubCategoryLabel(s).toLowerCase() === trimmed.toLowerCase()
  );
  if (byTitle) {
    return String(byTitle.id);
  }
  return trimmed;
}

function schemeToForm(
  scheme: DonationScheme,
  subCategories: DonationSubCategoryMaster[] = []
): DonationSchemeFormValues {
  return {
    title: scheme.title ?? "",
    sub_title: scheme.sub_title ?? "",
    type_of_donation: scheme.type_of_donation
      ? String(scheme.type_of_donation)
      : "",
    category: scheme.category ? String(scheme.category) : "",
    sub_category: resolveSubCategoryFormValue(
      scheme.sub_category,
      subCategories
    ),
    description: scheme.description ?? "",
    amount_needed: scheme.amount_needed ? String(scheme.amount_needed) : "",
    is_active: scheme.is_active ?? true,
    start_date: scheme.start_date?.slice(0, 10) ?? "",
    end_date: scheme.end_date?.slice(0, 10) ?? "",
    before_details: scheme.before_details ?? "",
    after_details: scheme.after_details ?? "",
    allow_donation_after_target: scheme.allow_donation_after_target ?? false,
    is_cash_donation: scheme.is_cash_donation ?? true,
    amount_type: normalizeAmountType(scheme.amount_type),
    fixed_amount: scheme.fixed_amount ? String(scheme.fixed_amount) : "",
    min_contribution: scheme.min_contribution
      ? String(scheme.min_contribution)
      : "",
    max_contribution: scheme.max_contribution
      ? String(scheme.max_contribution)
      : "",
    bannerFile: null,
    beforeMediaFiles: [],
    afterMediaFiles: [],
    project: scheme.project ? String(scheme.project) : "",
  };
}

function getSchemeBannerUrl(scheme: DonationScheme): string | null {
  const record = scheme as unknown as Record<string, unknown>;
  const raw = scheme.banner_image ?? scheme.banner ?? record.banner_url;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  return null;
}

export default function DonationCampaignsPage() {
  const [schemes, setSchemes] = useState<DonationScheme[]>([]);
  const [donationTypes, setDonationTypes] = useState<DonationTypeMaster[]>([]);
  const [mainCategories, setMainCategories] = useState<
    DonationMainCategoryMaster[]
  >([]);
  const [subCategories, setSubCategories] = useState<
    DonationSubCategoryMaster[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<DonationSchemeFormValues>(initialFormState);
  const [beforeMediaItems, setBeforeMediaItems] = useState<MediaPreviewItem[]>(
    []
  );
  const [afterMediaItems, setAfterMediaItems] = useState<MediaPreviewItem[]>(
    []
  );
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loadingEditDetail, setLoadingEditDetail] = useState(false);
  const [editing, setEditing] = useState<DonationScheme | null>(null);
  const [deleting, setDeleting] = useState<DonationScheme | null>(null);
  const [viewing, setViewing] = useState(false);
  const [viewDetail, setViewDetail] = useState<DonationScheme | null>(null);
  const [loadingViewDetail, setLoadingViewDetail] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Program/Project dropdown state
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [linkToProject, setLinkToProject] = useState(false);

  const resetForm = () => {
    setBeforeMediaItems(prev => {
      revokeBlobUrls(prev);
      return [];
    });
    setAfterMediaItems(prev => {
      revokeBlobUrls(prev);
      return [];
    });
    if (bannerPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(bannerPreview);
    }
    setBannerPreview(null);
    setLinkToProject(false);
    setForm(initialFormState);
    setEditing(null);
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Banner must be an image file");
      return;
    }
    if (bannerPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(bannerPreview);
    }
    setForm(prev => ({ ...prev, bannerFile: file }));
    setBannerPreview(URL.createObjectURL(file));
  };

  const removeBanner = () => {
    if (bannerPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(bannerPreview);
    }
    setBannerPreview(null);
    setForm(prev => ({
      ...prev,
      bannerFile: null,
      retainedBannerUrl: undefined,
    }));
  };

  const filteredSubCategories = useMemo(() => {
    if (!form.category) {
      return [];
    }
    return subCategories.filter(
      s => String(s.main_category) === String(form.category)
    );
  }, [subCategories, form.category]);

  const loadMasters = useCallback(async () => {
    setLoadingMasters(true);
    try {
      const [types, categories, subCats] = await Promise.all([
        donationTypeMasterAPI.list(),
        donationMainCategoryMasterAPI.list(),
        donationSubCategoryMasterAPI.list(),
      ]);
      setDonationTypes(types.filter(t => t.is_active !== false));
      setMainCategories(categories.filter(c => c.is_active !== false));
      setSubCategories(subCats.filter(s => s.is_active !== false));
    } catch {
      toast.error("Failed to load donation masters");
    } finally {
      setLoadingMasters(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await donationSchemesAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setSchemes(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load donation campaigns");
      setSchemes([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const res = await projectsAPI.list();
      if (res && typeof res === "object") {
        const list = res.results ?? res.data ?? [];
        setProjects(Array.isArray(list) ? list : []);
      }
    } catch {
      toast.error("Failed to load programs");
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    void loadMasters();
    void loadProjects();
  }, [loadMasters, loadProjects]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchList();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleMediaChange = (
    phase: CampaignMediaPhase,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!selected.length) {
      return;
    }

    const setItems =
      phase === "before" ? setBeforeMediaItems : setAfterMediaItems;
    const currentItems =
      phase === "before" ? beforeMediaItems : afterMediaItems;
    const remainingSlots = MAX_CAMPAIGN_MEDIA - currentItems.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_CAMPAIGN_MEDIA} media files allowed`);
      return;
    }

    const toAdd = selected.slice(0, remainingSlots);
    if (selected.length > remainingSlots) {
      toast.error(
        `Only ${remainingSlots} more file(s) can be added (max ${MAX_CAMPAIGN_MEDIA})`
      );
    }

    const newItems: MediaPreviewItem[] = toAdd.map(file => ({
      id: `new-${file.name}-${file.lastModified}-${Math.random()}`,
      url: URL.createObjectURL(file),
      isVideo: isVideoFile(file),
      isNew: true,
      file,
    }));

    setItems(prev => [...prev, ...newItems]);
  };

  const removeMediaItem = (phase: CampaignMediaPhase, id: string) => {
    const setItems =
      phase === "before" ? setBeforeMediaItems : setAfterMediaItems;
    setItems(prev => {
      const target = prev.find(item => item.id === id);
      if (target?.isNew && target.url.startsWith("blob:")) {
        URL.revokeObjectURL(target.url);
      }
      return prev.filter(item => item.id !== id);
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.type_of_donation) {
      toast.error("Donation type is required");
      return;
    }
    if (!form.category) {
      toast.error("Main category is required");
      return;
    }
    if (!form.amount_needed.trim() || Number(form.amount_needed) <= 0) {
      toast.error("Valid amount needed is required");
      return;
    }
    const amountNeededError = getAmountRangeError(
      form.amount_needed,
      "Amount needed"
    );
    if (amountNeededError) {
      toast.error(amountNeededError);
      return;
    }
    if (!editing && !form.bannerFile && !bannerPreview) {
      toast.error("Banner image is required");
      return;
    }
    if (!form.start_date) {
      toast.error("Start date is required");
      return;
    }
    if (!form.end_date) {
      toast.error("End date is required");
      return;
    }
    if (form.start_date > form.end_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    if (form.amount_type === "fixed") {
      if (!form.fixed_amount.trim() || Number(form.fixed_amount) <= 0) {
        toast.error("Fixed amount is required for fixed amount type");
        return;
      }
      const fixedAmountError = getAmountRangeError(
        form.fixed_amount,
        "Fixed amount"
      );
      if (fixedAmountError) {
        toast.error(fixedAmountError);
        return;
      }
    } else {
      if (!form.min_contribution.trim() || Number(form.min_contribution) <= 0) {
        toast.error(
          "Minimum contribution is required for variable amount type"
        );
        return;
      }
      if (!form.max_contribution.trim() || Number(form.max_contribution) <= 0) {
        toast.error(
          "Maximum contribution is required for variable amount type"
        );
        return;
      }
      const minError = getAmountRangeError(
        form.min_contribution,
        "Minimum contribution"
      );
      if (minError) {
        toast.error(minError);
        return;
      }
      const maxError = getAmountRangeError(
        form.max_contribution,
        "Maximum contribution"
      );
      if (maxError) {
        toast.error(maxError);
        return;
      }
      if (Number(form.min_contribution) > Number(form.max_contribution)) {
        toast.error("Minimum contribution cannot exceed maximum contribution");
        return;
      }
    }

    const retainedBannerUrl =
      !form.bannerFile && bannerPreview && !bannerPreview.startsWith("blob:")
        ? bannerPreview
        : undefined;
    const savePayload = {
      ...form,
      bannerFile: form.bannerFile ?? null,
      retainedBannerUrl,
    };

    const totalMedia = beforeMediaItems.length + afterMediaItems.length;
    if (!editing && totalMedia === 0) {
      toast.error("At least one campaign media file is required");
      return;
    }
    if (
      beforeMediaItems.length > MAX_CAMPAIGN_MEDIA ||
      afterMediaItems.length > MAX_CAMPAIGN_MEDIA
    ) {
      toast.error(`Maximum ${MAX_CAMPAIGN_MEDIA} media files per section`);
      return;
    }

    const newBeforeMediaFiles = beforeMediaItems
      .filter(item => item.isNew && item.file)
      .map(item => item.file as File);
    const newAfterMediaFiles = afterMediaItems
      .filter(item => item.isNew && item.file)
      .map(item => item.file as File);
    const {
      retainedMediaIds: retainedBeforeMediaIds,
      retainedMediaUrls: retainedBeforeMediaUrls,
    } = buildRetainedMediaPayload(beforeMediaItems);
    const {
      retainedMediaIds: retainedAfterMediaIds,
      retainedMediaUrls: retainedAfterMediaUrls,
    } = buildRetainedMediaPayload(afterMediaItems);

    setSubmitLoading(true);
    try {
      if (editing) {
        if (totalMedia === 0) {
          toast.error("Keep at least one campaign media file");
          setSubmitLoading(false);
          return;
        }

        await donationSchemesAPI.update(editing.id, {
          ...savePayload,
          beforeMediaFiles: newBeforeMediaFiles,
          afterMediaFiles: newAfterMediaFiles,
          retainedBeforeMediaIds,
          retainedBeforeMediaUrls,
          retainedAfterMediaIds,
          retainedAfterMediaUrls,
        });
        toast.success("Donation campaign updated successfully");
      } else {
        await donationSchemesAPI.create({
          ...savePayload,
          beforeMediaFiles: newBeforeMediaFiles,
          afterMediaFiles: newAfterMediaFiles,
        });
        toast.success("Donation campaign created successfully");
      }
      setDialogOpen(false);
      resetForm();
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        void fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err, "Failed to save campaign")));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await donationSchemesAPI.delete(deleting.id);
      toast.success("Donation campaign deleted successfully");
      setDeleting(null);
      if (schemes.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        void fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err, "Failed to delete campaign")));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openView = useCallback(async (scheme: DonationScheme) => {
    setViewing(true);
    setViewDetail(null);
    setLoadingViewDetail(true);
    try {
      const detail = (await donationSchemesAPI.get(scheme.id)) ?? scheme;
      setViewDetail(detail);
    } catch {
      toast.error("Failed to load campaign details");
      setViewing(false);
      setViewDetail(null);
    } finally {
      setLoadingViewDetail(false);
    }
  }, []);

  const closeView = useCallback(() => {
    setViewing(false);
    setViewDetail(null);
  }, []);

  const openEdit = useCallback(
    async (scheme: DonationScheme) => {
      setDialogOpen(true);
      setLoadingEditDetail(true);
      setEditing(scheme);
      try {
        const detail = (await donationSchemesAPI.get(scheme.id)) ?? scheme;
        setEditing(detail);
        setForm(schemeToForm(detail, subCategories));
        setLinkToProject(!!detail.project);
        setBannerPreview(getSchemeBannerUrl(detail));
        setBeforeMediaItems(
          parseSchemeMedia(detail, "before").map(item => ({
            ...item,
            isNew: false,
          }))
        );
        setAfterMediaItems(
          parseSchemeMedia(detail, "after").map(item => ({
            ...item,
            isNew: false,
          }))
        );
      } catch {
        toast.error("Failed to load campaign details");
        setDialogOpen(false);
        setEditing(null);
      } finally {
        setLoadingEditDetail(false);
      }
    },
    [subCategories]
  );

  const resolveDonationTypeName = (scheme: DonationScheme) =>
    scheme.type_of_donation_name ??
    donationTypes.find(t => String(t.id) === String(scheme.type_of_donation))
      ?.name ??
    "—";

  const resolveCategoryName = (scheme: DonationScheme) =>
    scheme.category_name ??
    mainCategories.find(c => String(c.id) === String(scheme.category))?.name ??
    "—";

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Donation Campaigns
        </h1>
        <p className="text-muted-foreground">
          Create and manage donation schemes / fundraising campaigns
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
          <CardTitle>Campaigns ({totalItems})</CardTitle>
          <Button className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Create Campaign
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 p-3 pt-0">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10"
            />
          </div>

          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <CustomLoader size="md" />
            </div>
          ) : schemes.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              {totalItems === 0 && !searchTerm.trim()
                ? "No campaigns yet. Create one to get started."
                : "No campaigns found matching your search."}
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {schemes.map(scheme => (
                <DonationCampaignListCard
                  key={scheme.id}
                  scheme={scheme}
                  onView={scheme => void openView(scheme)}
                  onEdit={row => void openEdit(row)}
                  onDelete={setDeleting}
                />
              ))}
            </div>
          )}

          {totalItems > 0 && (
            <div className="border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={open => {
          setDialogOpen(open);
          if (!open) {
            resetForm();
          }
        }}
      >
        <DialogContent className="flex max-h-[min(92vh,900px)] w-full max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl [&>button]:right-4 [&>button]:top-4">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-4 pr-12">
            <DialogTitle>
              {editing ? "Edit Donation Campaign" : "Create Donation Campaign"}
            </DialogTitle>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-6 py-4">
            {loadingEditDetail ? (
              <div className="flex h-48 items-center justify-center">
                <CustomLoader size="md" />
              </div>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="campaign-title">Title *</Label>
                      <Input
                        id="campaign-title"
                        value={form.title}
                        onChange={e =>
                          setForm(prev => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                        placeholder="Build New Library"
                      />
                    </div>
                    <div>
                      <Label htmlFor="campaign-sub-title">Sub Title</Label>
                      <Input
                        id="campaign-sub-title"
                        value={form.sub_title}
                        onChange={e =>
                          setForm(prev => ({
                            ...prev,
                            sub_title: e.target.value,
                          }))
                        }
                        placeholder="Help us expand learning space"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="campaign-banner">
                      Banner Image {!editing && "*"}
                    </Label>
                    <div className="relative mt-1.5 h-36 w-full shrink-0 overflow-hidden rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20">
                      <input
                        id="campaign-banner"
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleBannerChange}
                      />
                      {bannerPreview ? (
                        <>
                          <img
                            src={bannerPreview}
                            alt="Campaign banner preview"
                            className="absolute inset-0 h-full w-full object-cover object-center"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            className="absolute right-2 top-2 z-10 h-7 w-7 shadow-sm"
                            onClick={removeBanner}
                            aria-label="Remove banner"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <label
                            htmlFor="campaign-banner"
                            className="absolute inset-x-0 bottom-0 z-10 flex cursor-pointer items-center justify-center gap-1.5 bg-black/50 py-2 text-xs font-medium text-white transition-colors hover:bg-black/60"
                          >
                            <ImagePlus className="h-3.5 w-3.5" />
                            Change image
                          </label>
                        </>
                      ) : (
                        <label
                          htmlFor="campaign-banner"
                          className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-2 px-4 text-center text-muted-foreground transition-colors hover:bg-muted/40"
                        >
                          <ImagePlus className="h-8 w-8 text-primary/80" />
                          <span className="text-sm font-medium">
                            Choose image...
                          </span>
                        </label>
                      )}
                    </div>
                    {/* <p className="mt-1.5 text-xs text-muted-foreground">
                      Upload a single image for the campaign banner on the
                      public site.
                    </p> */}
                  </div>
                  <div>
                    <Label>Donation Type *</Label>
                    <Select
                      value={form.type_of_donation || undefined}
                      onValueChange={v =>
                        setForm(prev => ({ ...prev, type_of_donation: v }))
                      }
                      disabled={loadingMasters}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select donation type" />
                      </SelectTrigger>
                      <SelectContent>
                        {donationTypes.map(t => (
                          <SelectItem key={t.id} value={String(t.id)}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Main Category *</Label>
                    <Select
                      value={form.category || undefined}
                      onValueChange={v =>
                        setForm(prev => ({
                          ...prev,
                          category: v,
                          sub_category: "",
                        }))
                      }
                      disabled={loadingMasters}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select main category" />
                      </SelectTrigger>
                      <SelectContent>
                        {mainCategories.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Sub Category</Label>
                    <Select
                      value={form.sub_category || undefined}
                      onValueChange={v =>
                        setForm(prev => ({ ...prev, sub_category: v }))
                      }
                      disabled={
                        loadingMasters ||
                        !form.category ||
                        filteredSubCategories.length === 0
                      }
                    >
                      <SelectTrigger id="campaign-sub-category">
                        <SelectValue
                          placeholder={
                            !form.category
                              ? "Select main category first"
                              : filteredSubCategories.length === 0
                                ? "No sub categories for this main category"
                                : "Select sub category"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredSubCategories.map(s => (
                          <SelectItem key={s.id} value={String(s.id)}>
                            {getSubCategoryLabel(s)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2 py-1 sm:col-span-2">
                    <Checkbox
                      id="linkToProject"
                      checked={linkToProject}
                      onCheckedChange={checked => {
                        setLinkToProject(checked === true);
                        if (!checked) {
                          setForm(prev => ({ ...prev, project: "" }));
                        }
                      }}
                    />
                    <Label
                      htmlFor="linkToProject"
                      className="cursor-pointer text-sm font-normal"
                    >
                      Associate this campaign with a Program/Initiative
                    </Label>
                  </div>

                  {linkToProject && (
                    <div className="sm:col-span-2">
                      <Label htmlFor="campaign-project">
                        Program / Initiative
                      </Label>
                      <Select
                        value={form.project || undefined}
                        onValueChange={v =>
                          setForm(prev => ({ ...prev, project: v }))
                        }
                        disabled={loadingProjects}
                      >
                        <SelectTrigger id="campaign-project">
                          <SelectValue
                            placeholder={
                              loadingProjects
                                ? "Loading programs..."
                                : "Select program"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map(p => (
                            <SelectItem key={p.id} value={String(p.id)}>
                              {p.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="sm:col-span-2">
                    <Label htmlFor="campaign-description">Description</Label>
                    <Textarea
                      id="campaign-description"
                      value={form.description}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Fundraising for library construction"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="campaign-amount-needed">
                      Amount Needed *
                    </Label>
                    <Input
                      id="campaign-amount-needed"
                      type="number"
                      min="1"
                      value={form.amount_needed}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          amount_needed: e.target.value,
                        }))
                      }
                      placeholder="500000"
                      aria-invalid={Boolean(
                        getAmountRangeError(form.amount_needed, "Amount needed")
                      )}
                      className={
                        getAmountRangeError(form.amount_needed, "Amount needed")
                          ? "border-destructive"
                          : undefined
                      }
                    />
                    <AmountFieldMessage
                      value={form.amount_needed}
                      label="Amount needed"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Amount Type *</Label>
                    <Select
                      value={form.amount_type}
                      onValueChange={(v: DonationSchemeAmountType) =>
                        setForm(prev => ({
                          ...prev,
                          amount_type: v,
                          fixed_amount: v === "fixed" ? prev.fixed_amount : "",
                          min_contribution:
                            v === "variable" ? prev.min_contribution : "",
                          max_contribution:
                            v === "variable" ? prev.max_contribution : "",
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="variable">Variable</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {form.amount_type === "fixed"
                        ? "Donors contribute a single fixed amount."
                        : "Donors choose their amount within min and max limits per contribution."}
                    </p>
                  </div>
                  {form.amount_type === "fixed" ? (
                    <div className="sm:col-span-2">
                      <Label htmlFor="campaign-fixed-amount">
                        Fixed Amount (per contribution) *
                      </Label>
                      <Input
                        id="campaign-fixed-amount"
                        type="number"
                        min="1"
                        value={form.fixed_amount}
                        onChange={e =>
                          setForm(prev => ({
                            ...prev,
                            fixed_amount: e.target.value,
                          }))
                        }
                        placeholder="1000"
                        aria-invalid={Boolean(
                          getAmountRangeError(form.fixed_amount, "Fixed amount")
                        )}
                        className={
                          getAmountRangeError(form.fixed_amount, "Fixed amount")
                            ? "border-destructive"
                            : undefined
                        }
                      />
                      <AmountFieldMessage
                        value={form.fixed_amount}
                        label="Fixed amount"
                      />
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label htmlFor="campaign-min-contribution">
                          Min Contribution (per donation) *
                        </Label>
                        <Input
                          id="campaign-min-contribution"
                          type="number"
                          min="1"
                          value={form.min_contribution}
                          onChange={e =>
                            setForm(prev => ({
                              ...prev,
                              min_contribution: e.target.value,
                            }))
                          }
                          placeholder="100"
                          aria-invalid={Boolean(
                            getAmountRangeError(
                              form.min_contribution,
                              "Minimum contribution"
                            )
                          )}
                          className={
                            getAmountRangeError(
                              form.min_contribution,
                              "Minimum contribution"
                            )
                              ? "border-destructive"
                              : undefined
                          }
                        />
                        <AmountFieldMessage
                          value={form.min_contribution}
                          label="Minimum contribution"
                        />
                      </div>
                      <div>
                        <Label htmlFor="campaign-max-contribution">
                          Max Contribution (per donation) *
                        </Label>
                        <Input
                          id="campaign-max-contribution"
                          type="number"
                          min="1"
                          value={form.max_contribution}
                          onChange={e =>
                            setForm(prev => ({
                              ...prev,
                              max_contribution: e.target.value,
                            }))
                          }
                          placeholder="50000"
                          aria-invalid={Boolean(
                            getAmountRangeError(
                              form.max_contribution,
                              "Maximum contribution"
                            )
                          )}
                          className={
                            getAmountRangeError(
                              form.max_contribution,
                              "Maximum contribution"
                            )
                              ? "border-destructive"
                              : undefined
                          }
                        />
                        <AmountFieldMessage
                          value={form.max_contribution}
                          label="Maximum contribution"
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <Label htmlFor="campaign-start-date">Start Date *</Label>
                    <Input
                      id="campaign-start-date"
                      type="date"
                      value={form.start_date}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          start_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="campaign-end-date">End Date *</Label>
                    <Input
                      id="campaign-end-date"
                      type="date"
                      value={form.end_date}
                      onChange={e =>
                        setForm(prev => ({ ...prev, end_date: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                    <div>
                      <Label>Active</Label>
                      <p className="text-xs text-muted-foreground">
                        {form.is_active
                          ? "Campaign is live"
                          : "Campaign is hidden"}
                      </p>
                    </div>
                    <Switch
                      checked={form.is_active}
                      onCheckedChange={checked =>
                        setForm(prev => ({ ...prev, is_active: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                    <div>
                      <Label>Cash Donation</Label>
                      <p className="text-xs text-muted-foreground">
                        Accept cash donations
                      </p>
                    </div>
                    <Switch
                      checked={form.is_cash_donation}
                      onCheckedChange={checked =>
                        setForm(prev => ({
                          ...prev,
                          is_cash_donation: checked,
                        }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border px-4 py-3 sm:col-span-2">
                    <div>
                      <Label>Allow donation after target</Label>
                      <p className="text-xs text-muted-foreground">
                        Continue accepting when goal is reached
                      </p>
                    </div>
                    <Switch
                      checked={form.allow_donation_after_target}
                      onCheckedChange={checked =>
                        setForm(prev => ({
                          ...prev,
                          allow_donation_after_target: checked,
                        }))
                      }
                    />
                  </div>
                </div>

                <Tabs defaultValue="before" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="before">Before</TabsTrigger>
                    <TabsTrigger value="after">After</TabsTrigger>
                  </TabsList>
                  <TabsContent value="before" className="space-y-4 pt-2">
                    <div>
                      <Label htmlFor="campaign-before">Before Details</Label>
                      <Textarea
                        id="campaign-before"
                        value={form.before_details}
                        onChange={e =>
                          setForm(prev => ({
                            ...prev,
                            before_details: e.target.value,
                          }))
                        }
                        rows={4}
                      />
                    </div>
                    <CampaignMediaUploadSection
                      phase="before"
                      items={beforeMediaItems}
                      required={!editing}
                      onFileChange={handleMediaChange}
                      onRemove={removeMediaItem}
                    />
                  </TabsContent>
                  <TabsContent value="after" className="space-y-4 pt-2">
                    <div>
                      <Label htmlFor="campaign-after">After Details</Label>
                      <Textarea
                        id="campaign-after"
                        value={form.after_details}
                        onChange={e =>
                          setForm(prev => ({
                            ...prev,
                            after_details: e.target.value,
                          }))
                        }
                        rows={4}
                      />
                    </div>
                    <CampaignMediaUploadSection
                      phase="after"
                      items={afterMediaItems}
                      required={false}
                      onFileChange={handleMediaChange}
                      onRemove={removeMediaItem}
                    />
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={submitLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={submitLoading || loadingEditDetail}
            >
              {submitLoading ? (
                <CustomLoader size="xs" />
              ) : editing ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DonationCampaignDetailsModal
        open={viewing}
        loading={loadingViewDetail}
        scheme={viewDetail}
        donationTypeName={
          viewDetail ? resolveDonationTypeName(viewDetail) : "—"
        }
        categoryName={viewDetail ? resolveCategoryName(viewDetail) : "—"}
        onOpenChange={open => !open && closeView()}
        onEdit={scheme => {
          closeView();
          void openEdit(scheme);
        }}
      />

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Campaign</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{" "}
                <strong>{deleting.title}</strong>?
              </p>
              <DialogFooter className="gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void handleDelete()}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
